import React, { useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { demoTemplates } from '../data/demoTemplates';

// Loading spinner component
const Spinner = ({ className = '', size = 'md' }) => {
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-16 w-16',
    lg: 'h-20 w-20'
  }[size] || 'h-16 w-16';
    
  return (
    <div className={`relative ${className}`}>
      <div className={`flex flex-col items-center`}>
        <div 
          className={`animate-spin rounded-full ${sizeClasses}`}
          style={{
            border: '6px solid rgba(0, 0, 0, 0.1)',
            borderTop: '6px solid rgb(211, 52, 94)',
            borderRight: '6px solid rgba(0, 0, 0, 0.1)',
            borderBottom: '6px solid rgba(0, 0, 0, 0.1)',
            animation: 'spin 0.8s linear infinite',
          }}
        ></div>
      </div>
      <style jsx global>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// Loading overlay for preview section
const PreviewLoadingOverlay = ({ message = 'Generating preview...' }) => (
  <div className="absolute inset-0 bg-white bg-opacity-90 flex flex-col items-center justify-center rounded-lg z-10 p-4">
    <div className="flex flex-col items-center -mt-12">
      <Spinner size="lg" className="mb-6" />
      <p className="text-gray-700 font-medium text-center">{message}</p>
    </div>
  </div>
);

// Import the Personalia client
let PersonaliaClient;

// Initialize the client when the component mounts
const initializeClient = async () => {
  if (typeof window !== 'undefined' && !PersonaliaClient) {
    try {
      // Import the client using the correct path from the example
      const module = await import('personalia-node');
      // The example shows using named import { PersonaliaClient }
      PersonaliaClient = module.PersonaliaClient;
      
      if (!PersonaliaClient) {
        throw new Error('Could not find PersonaliaClient in the module');
      }
      
      console.log('Personalia client initialized');
      return true;
    } catch (err) {
      console.error('Failed to initialize Personalia client:', err);
      throw err; // Re-throw to be handled by the caller
    }
  }
  return !!PersonaliaClient;
};

// Create a new client instance with the API key
const createClient = () => {
  if (typeof window === 'undefined') {
    throw new Error('Cannot create client on server side');
  }
  if (!process.env.NEXT_PUBLIC_PERSONALIA_API_KEY) {
    throw new Error('API key is not configured');
  }
  return new PersonaliaClient(process.env.NEXT_PUBLIC_PERSONALIA_API_KEY);
};

// Helper function to normalize form data for Personalia API
const normalizeFormData = (data) => {
  const normalized = {};
  
  Object.entries(data).forEach(([key, value]) => {
    if (typeof value === 'boolean') {
      normalized[key] = value ? '1' : '0';
    } else if (value === null || value === undefined) {
      normalized[key] = ''; // Convert null/undefined to empty string
    } else {
      // Convert all other values to string
      normalized[key] = String(value);
    }
  });
  
  return normalized;
};

const LiveDemoSection = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(demoTemplates[0]);
  const [formData, setFormData] = useState({});
  const [previewUrls, setPreviewUrls] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [fileInfo, setFileInfo] = useState({
    size: null,
    generationTime: null,
    dimensions: { width: null, height: null },
    totalPages: 0
  });
  const [generationStartTime, setGenerationStartTime] = useState(null);
  const [generationEndTime, setGenerationEndTime] = useState(null);
  
  // Panning state
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const containerRef = useRef(null);
  
  // Set up touch device detection after component mounts
  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window);
  }, []);
  
  // Handle start of drag (mouse or touch)
  const handleDragStart = (e) => {
    if (zoomLevel <= 1) return;
    
    const clientX = e.clientX || e.touches[0].clientX;
    const clientY = e.clientY || e.touches[0].clientY;
    
    setIsDragging(true);
    setDragStart({
      x: clientX - dragOffsetRef.current.x,
      y: clientY - dragOffsetRef.current.y
    });
    
    // Prevent default to avoid text selection and scrolling
    e.preventDefault();
    e.stopPropagation();
  };
  
  // Handle drag movement (mouse or touch)
  const handleDragMove = (e) => {
    if (!isDragging || zoomLevel <= 1) return;
    
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    
    if (clientX === undefined || clientY === undefined) return;
    
    const x = clientX - dragStart.x;
    const y = clientY - dragStart.y;
    
    // Update the ref immediately for smooth dragging
    dragOffsetRef.current = { x, y };
    
    // Batch the state update for better performance
    setPosition({ x, y });
    
    // Prevent default to avoid scrolling
    e.preventDefault();
    e.stopPropagation();
  };
  
  // Handle end of drag (mouse or touch)
  const handleDragEnd = (e) => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // Prevent default to avoid any unwanted behavior
    e.preventDefault();
    e.stopPropagation();
  };
  
  // Reset position when zoom level changes
  useEffect(() => {
    if (zoomLevel <= 1) {
      setPosition({ x: 0, y: 0 });
      dragOffsetRef.current = { x: 0, y: 0 };
    }
  }, [zoomLevel]);
  
  // Add and remove event listeners
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const container = containerRef.current;
    if (!container) return;
    
    // Mouse event listeners
    container.addEventListener('mousedown', handleDragStart);
    window.addEventListener('mousemove', handleDragMove);
    window.addEventListener('mouseup', handleDragEnd);
    
    // Touch event listeners
    container.addEventListener('touchstart', handleDragStart, { passive: false });
    window.addEventListener('touchmove', handleDragMove, { passive: false });
    window.addEventListener('touchend', handleDragEnd, { passive: false });
    
    return () => {
      // Clean up mouse events
      container.removeEventListener('mousedown', handleDragStart);
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      
      // Clean up touch events
      container.removeEventListener('touchstart', handleDragStart);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, zoomLevel]);
  
  // Track generation start time
  useEffect(() => {
    if (isGenerating) {
      setGenerationStartTime(performance.now());
    }
  }, [isGenerating]);
  
  // Handle zoom functionality
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  };
  
  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  };
  
  const handleZoomReset = () => {
    setZoomLevel(1);
  };
  
  // Get file size in a readable format
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  // Update file info when preview URLs change
  useEffect(() => {
    console.log('previewUrls changed:', previewUrls);
    if (!previewUrls.length) return;
    
    const updateFileInfo = async () => {
      try {
        let totalSize = 0;
        let dimensions = [];
        
        // Process all pages
        for (const url of previewUrls) {
          const response = await fetch(url);
          const blob = await response.blob();
          totalSize += blob.size;
          
          // Get image dimensions
          const img = new Image();
          await new Promise((resolve) => {
            img.onload = () => {
              dimensions.push({
                width: img.width,
                height: img.height
              });
              resolve();
            };
            img.src = url;
          });
        }
        
        // Calculate generation time if we have both start and end times
        const generationTime = (generationStartTime && generationEndTime)
          ? ((generationEndTime - generationStartTime) / 1000).toFixed(2) + 's'
          : null;
        
        setFileInfo(prev => ({
          ...prev,
          size: totalSize,
          generationTime,
          dimensions: dimensions[0] || { width: null, height: null },
          totalPages: previewUrls.length
        }));
        
      } catch (err) {
        console.error('Error getting file info:', err);
      }
    };
    
    updateFileInfo();
  }, [previewUrls, generationStartTime, generationEndTime]);
  
  // Reset current page when URLs change
  useEffect(() => {
    setCurrentPage(0);
  }, [previewUrls]);

  // Function to check and set preview image for a template
  const checkAndSetPreviewImage = (template) => {
    const previewImagePath = `/images/${template.templateID}.png`;
    // Try to load the image to check if it exists
    const img = new Image();
    img.onload = () => setPreviewImage(previewImagePath);
    img.onerror = () => setPreviewImage(null);
    img.src = previewImagePath;
  };

  // Check for preview image on initial load
  useEffect(() => {
    checkAndSetPreviewImage(selectedTemplate);
  }, []);

  // Initialize form data with default values
  useEffect(() => {
    const initialData = {};
    selectedTemplate.templateFields.forEach(field => {
      if (field.defaultValue !== undefined) {
        // Handle function defaults (like date)
        initialData[field.name] = typeof field.defaultValue === 'function' 
          ? field.defaultValue() 
          : field.defaultValue;
      } else if (field.type === 'select' || field.type === 'radio') {
        initialData[field.name] = field.options[0].value;
      } else if (field.type === 'number') {
        initialData[field.name] = field.min || 0;
      } else {
        initialData[field.name] = '';
      }
    });
    setFormData(initialData);
    // Don't clear preview URL here as it causes the button to disable
  }, [selectedTemplate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const generateAsset = async (e) => {
    e.preventDefault();
    
    if (typeof window === 'undefined') {
      setError('Asset generation is only available in the browser');
      return;
    }

    setIsGenerating(true);
    setError('');
    setStatus('Generating preview...');
    setPreviewUrls([]);
    
    try {
      // Ensure the client is properly imported and initialized
      await initializeClient();
      
      // Create a new client instance
      const client = createClient();
      
      // Prepare the request data with high quality settings
      const requestData = {
        TemplateId: selectedTemplate.templateID,
        Fields: normalizeFormData(formData),
        Output: {
          Format: 'PNG',
          Resolution: 300,
          StrictPolicy: true
        }
      };
      
      // Create content request
      setStatus('Creating content...');
      const createResponse = await client.createContent(requestData);
      
      if (!createResponse.RequestId) {
        throw new Error('No request ID returned from Personalia');
      }
      
      // Poll for completion
      setStatus('Processing... (0s)');
      let attempts = 0;
      const maxAttempts = 30; // 30 attempts * 2s = 1 minute max wait time
      
      while (attempts < maxAttempts) {
        try {
          const contentResponse = await client.pollForContent(createResponse.RequestId);
          console.log('Poll response:', contentResponse);
          
          // Handle case where the response is a string that needs to be parsed
          let responseData = contentResponse;
          if (typeof contentResponse === 'string') {
            try {
              responseData = JSON.parse(contentResponse);
            } catch (e) {
              console.error('Failed to parse response as JSON:', contentResponse);
            }
          }
          
          if (responseData.Status === 'Completed' && responseData.URLs?.length) {
            // Set the end time as soon as we get the response
            setGenerationEndTime(performance.now());
            setPreviewUrls(responseData.URLs);
            setStatus('');
            return;
          } else if (responseData.Status === 'Failed') {
            console.log('Content generation failed with response:', responseData);
            
            // Handle error cases
            let errorMessage = 'Content generation failed';
            
            // Check for different possible error response structures
            if (responseData.FailureDescription) {
              const { Reason, ErrorId } = responseData.FailureDescription;
              
              // Map specific error IDs to user-friendly messages
              switch (ErrorId) {
                case 1009: // Text overflow
                  errorMessage = 'The text is too long for the available space. Please reduce the amount of text and try again.';
                  break;
                default:
                  errorMessage = Reason || `Content generation failed (Error ${ErrorId})`;
              }
            } else if (responseData.message) {
              errorMessage = responseData.message;
            } else if (responseData.error) {
              errorMessage = responseData.error;
            }
            
            const error = new Error(errorMessage);
            error.response = responseData;
            throw error;
          }
        } catch (pollError) {
          console.error('Error in pollForContent:', pollError);
          // Extract the error message from the pollError if it exists
          let errorMessage = 'Failed to check generation status. Please try again.';
          
          // Check if we have a response with FailureDescription
          if (pollError.response?.FailureDescription?.Reason) {
            errorMessage = pollError.response.FailureDescription.Reason;
          } 
          // Check if we have a message in the error
          else if (pollError.message) {
            errorMessage = pollError.message;
          }
          
          // Create a new error with the extracted message
          const error = new Error(errorMessage);
          // Preserve the original error details
          error.originalError = pollError;
          if (pollError.response) {
            error.response = pollError.response;
          }
          throw error;
        }
        
        setStatus(`Processing... (${(attempts * 2)}s)`);
        await new Promise(resolve => setTimeout(resolve, 2000));
        attempts++;
      }
      
      throw new Error('Content generation timed out after 1 minute');
      
    } catch (err) {
      console.error('Error in generateAsset:', err);
      console.log('Error object structure:', {
        message: err.message,
        name: err.name,
        stack: err.stack,
        response: err.response,
        originalError: err.originalError
      });
      
      // Default error message
      let errorMessage = 'An unknown error occurred';
      
      try {
        // First check the original error if it exists
        const errorToCheck = err.originalError || err;
        console.log('Error to check:', {
          message: errorToCheck.message,
          name: errorToCheck.name,
          response: errorToCheck.response,
          toString: errorToCheck.toString(),
          stringValue: String(errorToCheck)
        });
        
        // Try to get the error message from different sources
        if (errorToCheck.response?.FailureDescription?.Reason) {
          // Case 1: Error details in response.FailureDescription
          errorMessage = errorToCheck.response.FailureDescription.Reason;
        } 
        // Check if the error message contains a JSON object
        else if (errorToCheck.message && errorToCheck.message.includes('{') && errorToCheck.message.includes('}')) {
          try {
            const match = errorToCheck.message.match(/\{.*\}/s);
            if (match) {
              const errorObj = JSON.parse(match[0]);
              if (errorObj.FailureDescription?.Reason) {
                errorMessage = errorObj.FailureDescription.Reason;
              } else if (errorObj.message) {
                errorMessage = errorObj.message;
              }
            }
          } catch (e) {
            console.error('Error parsing error message:', e);
            errorMessage = errorToCheck.message;
          }
        }
        // Check the string representation of the error
        else if (errorToCheck.toString() !== '[object Object]') {
          const errorString = errorToCheck.toString();
          console.log('Error string representation:', errorString);
          
          // Try to extract a meaningful message from the string
          if (errorString.includes('Text overflow') || errorString.includes('1009')) {
            errorMessage = 'The text is too long for the available space. Please reduce the amount of text and try again.';
          } else if (errorString.includes('Content generation failed')) {
            errorMessage = 'The content could not be generated. Please check your input and try again.';
          } else {
            // Use the string representation as a last resort
            errorMessage = errorString;
          }
        }
        // Fall back to the error message if available
        else if (errorToCheck.message) {
          errorMessage = errorToCheck.message;
        }
        
        // Handle specific error cases
        if (errorMessage.includes('Text overflow') || errorMessage.includes('1009')) {
          errorMessage = 'The text is too long for the available space. Please reduce the amount of text and try again.';
        } else if (errorMessage.includes('Content generation failed')) {
          // If we have a more specific error message, use it
          if (errorToCheck.response?.FailureDescription?.Reason) {
            errorMessage = errorToCheck.response.FailureDescription.Reason;
          } else {
            errorMessage = 'The content could not be generated. Please check your input and try again.';
          }
        }
        
      } catch (parseErr) {
        console.error('Error parsing error message:', parseErr);
        errorMessage = 'An error occurred while generating the content. Please try again.';
      }
      
      // Clean up the error message
      if (errorMessage) {
        errorMessage = errorMessage
          .toString()
          .replace(/^Error: /, '')
          .replace(/^Failed to generate asset: /, '')
          .replace(/\[object Object\]/gi, '')
          .trim();
        
        // Capitalize first letter and ensure it ends with a period
        if (errorMessage.length > 0) {
          errorMessage = errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1);
          if (!errorMessage.endsWith('.') && !errorMessage.endsWith('!') && !errorMessage.endsWith('?')) {
            errorMessage += '.';
          }
        } else {
          errorMessage = 'An unknown error occurred. Please try again.';
        }
      } else {
        errorMessage = 'An unknown error occurred. Please try again.';
      }
      
      console.log('Setting error message:', errorMessage);
      setError(errorMessage || 'An unknown error occurred. Please try again.');
      setStatus('');
    } finally {
      setIsGenerating(false);
    }
  };
  


  const renderField = (field) => {
    switch (field.type) {
      case 'select':
        return (
          <div className="mb-4" key={field.name}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
            </label>
            <select
              name={field.name}
              value={formData[field.name] || ''}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              {field.options.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        );
        
      case 'string':
        return (
          <div className="mb-4" key={field.name}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
            </label>
            <input
              type="text"
              name={field.name}
              value={formData[field.name] || ''}
              onChange={handleInputChange}
              placeholder={field.placeholder || ''}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        );
      
      case 'boolean':
        return (
          <div className="mb-4" key={field.name}>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.label}
                </label>
                {field.description && (
                  <p className="text-xs text-gray-500">{field.description}</p>
                )}
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!!formData[field.name]}
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    [field.name]: !prev[field.name]
                  }));
                }}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[rgb(211,52,94)] focus:ring-offset-2 bg-gray-200`}
              >
                <span className="sr-only">{field.label}</span>
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-[rgb(211,52,94)] shadow ring-0 transition duration-200 ease-in-out ${formData[field.name] ? 'translate-x-5' : 'translate-x-0'}`}
                />
              </button>
            </div>
          </div>
        );
        
      // Keep radio case in case it's needed for other fields
      case 'radio':
        return (
          <div className="mb-4" key={field.name}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {field.label}
            </label>
            <div className="flex space-x-4">
              {field.options.map(option => (
                <label key={option.value} className="inline-flex items-center">
                  <input
                    type="radio"
                    name={field.name}
                    value={option.value}
                    checked={formData[field.name] === option.value}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="ml-2 text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        );
      
      case 'number':
        return (
          <div className="mb-4" key={field.name}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
            </label>
            <input
              type="number"
              name={field.name}
              min={field.min}
              max={field.max}
              step={field.step}
              value={formData[field.name] || ''}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        );
      
      case 'date':
        return (
          <div className="mb-4" key={field.name}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
            </label>
            <input
              type="date"
              name={field.name}
              value={formData[field.name] || ''}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        );
      
      default:
        return null;
    }
  };

  // Page navigation functions
  const nextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, previewUrls.length - 1));
  };

  const prevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 0));
  };

  // Handle template tab change
  const handleTemplateChange = (template) => {
    setSelectedTemplate(template);
    // Reset form data when changing templates
    const initialData = {};
    template.templateFields.forEach(field => {
      if (field.defaultValue !== undefined) {
        initialData[field.name] = field.defaultValue;
      } else if (field.type === 'date') {
        // Default to today's date for date fields
        initialData[field.name] = new Date().toISOString().split('T')[0];
      } else if (field.type === 'select' && field.options?.length) {
        // Default to first option for select fields
        initialData[field.name] = field.options[0].value;
      } else {
        initialData[field.name] = '';
      }
    });
    setFormData(initialData);
      // Reset preview and page when changing templates
    setPreviewUrls([]);
    setCurrentPage(0);
    
    // Check if there's a preview image for this template
    checkAndSetPreviewImage(template);
  };

  return (
    <section id="live-demo" className="py-16 bg-gray-50">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">See It In Action</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Experience Personalia's powerful features with our interactive demo.
          </p>
        </div>

        <div className="bg-gray-50 rounded-xl p-8 shadow-lg">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Form Section - Now first in DOM for mobile */}
            <div className="lg:w-1/2">
              <div className="mb-6">
                <div className="border-b border-gray-200 overflow-x-auto">
                  <nav className="-mb-px flex space-x-2 sm:space-x-4" aria-label="Tabs">
                    {demoTemplates.map((template, index) => (
                      <button
                        key={template.templateID}
                        onClick={() => handleTemplateChange(template)}
                        className={`whitespace-nowrap py-4 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm ${
                          selectedTemplate.templateID === template.templateID
                            ? 'border-[rgb(211,52,94)] text-[rgb(211,52,94)] font-semibold'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        Demo {index + 1}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
              
              <h3 className="text-xl font-semibold mb-2">{selectedTemplate.templateName}</h3>
              <p className="text-gray-600 mb-6">
                {selectedTemplate.templateDescription}
              </p>
              
              <form onSubmit={(e) => { e.preventDefault(); generateAsset(e); }}>
                {selectedTemplate.templateFields.map(field => renderField(field))}
                
                <div className="mt-6 space-y-3">
                  <button
                    type="button"
                    onClick={generateAsset}
                    disabled={isGenerating}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-[rgb(211,52,94)] hover:bg-[rgb(180,40,80)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(211,52,94)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {isGenerating ? 'Generating...' : 'Generate Asset'}
                  </button>
                </div>
              </form>
              
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-500 mb-2">Template ID:</h4>
                <code className="text-xs bg-gray-100 p-2 rounded break-all block">
                  {selectedTemplate.templateID}
                </code>
              </div>
            </div>

            {/* Preview Section - Moved to second in DOM but positioned on right */}
            <div className="lg:w-1/2 lg:sticky lg:top-8 lg:self-start">
              <h3 className="text-xl font-semibold mb-4">Preview</h3>
              <div className="bg-white p-4 rounded-lg border border-gray-200 flex-1 flex flex-col">
                {/* Preview Image Container */}
                <div 
                  className="flex-1 flex flex-col items-center justify-center relative min-h-[300px] overflow-hidden"
                >
                  {previewImage && !isGenerating && previewUrls.length === 0 ? (
                    <div 
                      className="w-full h-full flex items-center justify-center"
                      style={{
                        backgroundImage: `url(${previewImage})`,
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        aspectRatio: '1/1.41',
                        maxHeight: '400px'
                      }}
                    />
                  ) : error ? (
                    <div className="w-full p-4 bg-red-50 border border-red-200 rounded-lg shadow-sm">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 mt-0.5">
                          <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">Something went wrong</h3>
                          <div className="mt-1 text-sm text-red-700">
                            <p>{error}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : isGenerating ? (
                    <PreviewLoadingOverlay message="Generating asset..." />
                  ) : previewUrls.length > 0 ? (
                    <>
                      <div 
                        ref={containerRef}
                        className="relative w-full h-full flex items-center justify-center overflow-hidden touch-none select-none"
                        style={{
                          touchAction: 'none',
                          cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                        }}
                      >
                        <div 
                          className="flex-shrink-0 w-full h-full flex items-center justify-center select-none"
                          style={{
                            transform: `translate3d(${position.x}px, ${position.y}px, 0) scale(${zoomLevel})`,
                            transformOrigin: 'center',
                            transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                            width: '100%',
                            height: '100%',
                            backgroundImage: `url(${previewUrls[currentPage]})`,
                            backgroundSize: 'contain',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            userSelect: 'none',
                            touchAction: 'none',
                            maxHeight: '400px',
                            aspectRatio: '1/1.41', // Standard A4 aspect ratio
                            willChange: 'transform',
                            WebkitBackfaceVisibility: 'hidden',
                            WebkitTransform: 'translate3d(0,0,0)'
                          }}
                        />
                      </div>
                      
                      {/* Page Navigation */}
                      {previewUrls.length > 1 && (
                        <div className="flex items-center justify-center mt-4 space-x-4">
                          <button
                            onClick={prevPage}
                            disabled={currentPage === 0}
                            className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="Previous page"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </button>
                          
                          <span className="text-sm text-gray-600">
                            Page {currentPage + 1} of {previewUrls.length}
                          </span>
                          
                          <button
                            onClick={nextPage}
                            disabled={currentPage === previewUrls.length - 1}
                            className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="Next page"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center text-gray-500">
                      <p>Your preview will appear here</p>
                      <p className="text-sm mt-2">Fill in the form and click "Generate Asset"</p>
                    </div>
                  )}
                </div>
                
                {/* Zoom Controls */}
                {previewUrls.length > 0 && !isGenerating && (
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-4 border-t border-gray-100 pt-3 gap-2">
                    <div className="flex items-center space-x-2 w-full sm:w-auto justify-between sm:justify-start">
                      <div className="flex space-x-1">
                        <button 
                          onClick={handleZoomOut}
                          className="p-1.5 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700"
                          title="Zoom Out"
                          aria-label="Zoom out"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <span className="text-sm text-gray-600 flex items-center px-1">
                          {Math.round(zoomLevel * 100)}%
                        </span>
                        <button 
                          onClick={handleZoomIn}
                          className="p-1.5 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700"
                          title="Zoom In"
                          aria-label="Zoom in"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                      <button 
                        onClick={handleZoomReset}
                        className="p-1.5 text-xs sm:text-sm rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 px-2"
                        title="Reset Zoom"
                        aria-label="Reset zoom"
                      >
                        Reset
                      </button>
                    </div>
                    
                    {/* File Info */}
                    <div className="text-xs text-gray-500 w-full sm:w-auto text-left sm:text-right">
                      {fileInfo.size && (
                        <div className="flex flex-wrap gap-x-2 gap-y-1">
                          <span>Size: {formatFileSize(fileInfo.size)}</span>
                          {fileInfo.dimensions.width && fileInfo.dimensions.height && (
                            <span>• {fileInfo.dimensions.width} × {fileInfo.dimensions.height}px</span>
                          )}
                          {fileInfo.generationTime && (
                            <span>• {fileInfo.generationTime}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LiveDemoSection;
